import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Calendar, CheckCircle2, AlertCircle, Heart, Building2, Store } from "lucide-react";
import api, { FILE_BASE } from "../lib/api";
import { isLoggedIn, getRole } from "../lib/auth";
import Header from "../components/Header";
import Footer from "../components/Footer";

const PAYOUT_LABEL = {
  company: "Direkt till ditt företag",
  sponsorship: "Till sponsorpotten",
  platform: "Till plattformen",
};

export default function PhotographerOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoggedIn() || getRole() !== "photographer") {
      navigate("/photographer/login");
      return;
    }
    Promise.all([
      api.get("/photographers/me/orders"),
      api.get("/auth/me"),
    ])
      .then(([o, m]) => {
        setOrders(o.data);
        setMe(m.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate]);

  const totalEarned = orders.reduce((s, o) => s + (o.total_earned_sek || 0), 0);
  const totalSold = orders.reduce((s, o) => s + (o.items?.length || 0), 0);

  const PayoutIcon = me?.payout_type === "company" ? Building2 : me?.payout_type === "sponsorship" ? Heart : Store;

  return (
    <div className="App">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/photographer/dashboard" className="text-brand hover:underline text-sm mb-4 inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Tillbaka
        </Link>
        <p className="label-overline mb-2">Mina försäljningar</p>
        <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl mb-6">
          Ordrar & intäkter
        </h1>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          <div className="card-surface p-4">
            <ShoppingBag className="w-5 h-5 text-brand mb-2" />
            <p className="text-3xl font-display font-black text-white">{orders.length}</p>
            <p className="text-xs uppercase tracking-wider text-white/50">Ordrar</p>
          </div>
          <div className="card-surface p-4">
            <CheckCircle2 className="w-5 h-5 text-brand mb-2" />
            <p className="text-3xl font-display font-black text-white">{totalSold}</p>
            <p className="text-xs uppercase tracking-wider text-white/50">Sålda foton</p>
          </div>
          <div className="card-surface p-4 col-span-2 md:col-span-1">
            <PayoutIcon className="w-5 h-5 text-brand mb-2" />
            <p className="text-3xl font-display font-black text-brand">{totalEarned.toFixed(0)} kr</p>
            <p className="text-xs uppercase tracking-wider text-white/50">
              {me?.payout_type === "platform" ? "Plattformen tar 100%" : "Din andel · 90%"}
            </p>
          </div>
        </div>

        {/* Payout-type explainer */}
        {me && (
          <div className="card-surface p-4 mb-6 flex items-start gap-3" data-testid="payout-explainer">
            <PayoutIcon className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-white font-bold uppercase tracking-wider text-xs mb-1">Utbetalning</p>
              <p className="text-white/70">{PAYOUT_LABEL[me.payout_type] || PAYOUT_LABEL.company}</p>
              {me.payout_type === "company" && (
                <p className="text-white/50 mt-1 text-xs">Pengarna förs över direkt till ditt Stripe Connect-konto efter betalning. Plattformen tar 10% i avgift.</p>
              )}
              {me.payout_type === "sponsorship" && (
                <p className="text-white/50 mt-1 text-xs">90% av nettoförsäljningen läggs i en sponsringspott som plattformen administrerar för förare-stöd.</p>
              )}
              {me.payout_type === "platform" && (
                <p className="text-white/50 mt-1 text-xs">Plattformen tar 100% — inga utbetalningar till dig.</p>
              )}
              <Link to="/photographer/dashboard" className="text-brand hover:underline text-xs mt-1 inline-block">
                Ändra utbetalningstyp →
              </Link>
            </div>
          </div>
        )}

        {/* Orders */}
        {loading ? (
          <p className="text-white/50 text-center py-12">Laddar...</p>
        ) : orders.length === 0 ? (
          <div className="card-surface p-12 text-center" data-testid="orders-empty">
            <ShoppingBag className="w-12 h-12 mx-auto text-white/20 mb-4" />
            <p className="font-display uppercase text-2xl text-white mb-2">Inga försäljningar än</p>
            <p className="text-white/60">Sålda foton dyker upp här när första ordern kommer in.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="my-orders-list">
            {orders.map((o) => (
              <div key={o.order_id} className="card-surface p-5" data-testid={`order-${o.order_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <p className="font-display font-bold uppercase text-white">
                      Order #{o.order_id.slice(0, 8).toUpperCase()}
                    </p>
                    <p className="text-xs text-white/50 inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(o.created_at).toLocaleString("sv-SE")}
                      {o.customer_name && <> · {o.customer_name}</>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-display font-black text-2xl text-brand">{o.total_earned_sek.toFixed(0)} kr</p>
                    <p className="text-xs uppercase tracking-wider text-white/50">Din intäkt</p>
                  </div>
                </div>

                {/* Transfer status */}
                <div className="flex items-center gap-2 mb-3 text-xs">
                  {o.transfer_status === "completed" && (
                    <span className="inline-flex items-center gap-1 bg-green-700/40 text-green-400 px-2 py-1 uppercase font-bold tracking-wider">
                      <CheckCircle2 className="w-3 h-3" /> Utbetalt
                    </span>
                  )}
                  {o.transfer_status === "pending" && (
                    <span className="inline-flex items-center gap-1 bg-yellow-700/40 text-yellow-400 px-2 py-1 uppercase font-bold tracking-wider">
                      <AlertCircle className="w-3 h-3" /> Pågår
                    </span>
                  )}
                  {o.transfer_status === "failed" && (
                    <span className="inline-flex items-center gap-1 bg-red-700/40 text-red-400 px-2 py-1 uppercase font-bold tracking-wider">
                      <AlertCircle className="w-3 h-3" /> Misslyckades
                    </span>
                  )}
                  {o.transfer_status === "skipped" && (
                    <span className="inline-flex items-center gap-1 bg-white/5 text-white/60 px-2 py-1 uppercase font-bold tracking-wider">
                      Direktbetalning
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2">
                  {o.items.map((it) => (
                    <div key={it.photo_id} className="card-surface p-1 text-center">
                      <div className="aspect-square overflow-hidden bg-[#1a1a1a]">
                        <img src={`${FILE_BASE}${it.thumb_url}`} alt="" className="w-full h-full object-cover" />
                      </div>
                      <p className="text-[10px] text-white/50 mt-1">
                        {it.price.toFixed(0)} kr · <span className="text-brand">+{it.photographer_amount.toFixed(0)}</span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
